import React from 'react';
import { FieldArrayRenderProps, FormikContext, useFormikContext } from 'formik';
import { FormBuilderValues } from '../FormBuilder';
import { GetForm_vm_form_formItems_VOLUNTEER_FormItemFieldType as FieldFormItemType } from '../../__generated__/GetForm';
import { FieldTaskItemDialog } from '../../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/dialogs/field-task-item-dialog/FieldTaskItemDialog';

const TASK_TO_FORM_FIELD_TYPENAME: Record<string, string> = {
  VOLUNTEER_MultipleChoiceTaskFieldType: 'VOLUNTEER_FormMultipleChoiceTaskFieldType',
  VOLUNTEER_CheckboxTaskFieldType: 'VOLUNTEER_FormCheckboxTaskFieldType',
  VOLUNTEER_DropdownTaskFieldType: 'VOLUNTEER_FormDropdownTaskFieldType',
  VOLUNTEER_QuizTaskFieldType: 'VOLUNTEER_FormQuizTaskFieldType',
  VOLUNTEER_MatrixTaskFieldType: 'VOLUNTEER_FormMatrixTaskFieldType',
};

const FORM_TO_TASK_FIELD_TYPENAME: Record<string, string> = Object.fromEntries(
  Object.entries(TASK_TO_FORM_FIELD_TYPENAME).map(([k, v]) => [v, k])
);

interface Props {
  open: boolean;
  close: () => void;
  arrayHelpers: FieldArrayRenderProps;
  data: FieldFormItemType | null;
}

export const FieldFormItemDialog = (props: Props) => {
  const { open, close, arrayHelpers, data } = props;
  const formikContext = useFormikContext<FormBuilderValues>();

  const adaptedData = data
    ? ({
        ...data,
        taskItemId: data.formItemId,
        __typename: 'VOLUNTEER_TaskItemFieldType',
        field: data.field
          ? {
              ...data.field,
              __typename: FORM_TO_TASK_FIELD_TYPENAME[data.field.__typename] || data.field.__typename,
            }
          : data.field,
      } as any)
    : null;

  const adaptedContext: any = {
    ...formikContext,
    values: {
      ...formikContext.values,
      taskItems: formikContext.values.formItems,
    },
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => {
      if (field === 'taskItems') {
        const convertedByIdEntries = Object.entries(value.byId || {}).map(([key, item]: [string, any]) => {
          const { taskItemId, field: itemField, ...rest } = item;

          const convertedField = itemField
            ? {
                ...itemField,
                __typename: TASK_TO_FORM_FIELD_TYPENAME[itemField.__typename] || itemField.__typename,
                formFieldId: itemField.taskFieldId,
              }
            : undefined;

          if (convertedField && convertedField.formFieldId === undefined) {
            delete convertedField.formFieldId;
          }

          return [
            key,
            {
              ...rest,
              __typename: item.__typename?.replace('TaskItem', 'FormItem') || item.__typename,
              formItemId: taskItemId || key,
              field: convertedField,
            },
          ];
        });

        const updatedByTrigger = { ...(value.byTrigger || {}) };
        Object.entries(value.byId || {}).forEach(([itemId, item]: [string, any]) => {
          const itemField = item.field;
          if (!itemField?.dynamic) return;

          const taskFieldId = itemField.taskFieldId || '';
          const typeName = itemField.__typename || '';

          const ensureEntries = (options: any[], idKey: string, optionType: string) => {
            (options || []).forEach((option: any) => {
              const optionId = option[idKey];
              if (optionId && !updatedByTrigger[optionId]) {
                updatedByTrigger[optionId] = {
                  formItemId: item.taskItemId || itemId,
                  triggerItemIds: [],
                  taskFieldId,
                  optionType,
                };
              }
            });
          };

          if (typeName === 'VOLUNTEER_MultipleChoiceTaskFieldType') {
            ensureEntries(itemField.multipleChoiceOptions, 'multipleChoiceFieldOptionId', 'multipleChoice');
          } else if (typeName === 'VOLUNTEER_CheckboxTaskFieldType') {
            ensureEntries(itemField.checkboxOptions, 'checkboxFieldOptionId', 'checkbox');
          } else if (typeName === 'VOLUNTEER_DropdownTaskFieldType') {
            ensureEntries(itemField.dropdownOptions, 'dropdownFieldOptionId', 'dropdown');
          } else if (typeName === 'VOLUNTEER_QuizTaskFieldType') {
            ensureEntries(itemField.quizTaskFieldOptions, 'quizTaskFieldOptionId', 'quiz');
          }
        });

        const convertedValue = {
          ...value,
          byId: Object.fromEntries(convertedByIdEntries),
          byTrigger: updatedByTrigger,
        };
        formikContext.setFieldValue('formItems', convertedValue, shouldValidate);
      } else {
        formikContext.setFieldValue(field, value, shouldValidate);
      }
    },
  };

  return (
    <FormikContext.Provider value={adaptedContext}>
      <FieldTaskItemDialog open={open} close={close} arrayHelpers={arrayHelpers} data={adaptedData} isForm />
    </FormikContext.Provider>
  );
};
